import { parseAgencyAndStatusToEmailCode } from "../components/AgencyRequestScreen/AgencyRequestScreen";
import { formatPhone } from "../phone_number";
import { Agency } from "../system/AppState/AppAgenciesState";
import { AppCaregiver } from "../system/AppState/AppCaregiverState";
import { AppVersion } from "../version";
import { fmap } from "./fmap";
import { getPlatform } from "./getPlatform";

export function onHasNotch<T>(callback: () => T): T | null {
  if (getCSSIOSSuffix() !== "") {
    return callback();
  }

  return null;
}

function getIphoneVersionFromUserAgent() {
  const result = navigator.userAgent.match(
    /iPhone OS (?<major>\d+)_(?<minor>\d+)(_(?<patch>\d+))*/
  );

  if (
    result === null ||
    result.groups === undefined ||
    typeof result.groups.major !== "string" ||
    typeof result.groups.minor !== "string"
  ) {
    return null;
  }

  return {
    major: parseInt(result.groups.major, 10),
    minor: parseInt(result.groups.minor, 10),
    patch: fmap(result.groups.patch, (x) => parseInt(x, 10)) ?? 0,
  };
}

export function getCSSIOSSuffix(): "" | "_notch11" | "_notch11_3" {
  if ((window as any).device !== undefined) {
    const deviceInfo = (window as any).device;
    // this needs to eventually support android notch too.... no current way to check though
    if (/iOS|iPhone|iPad/.test(deviceInfo.platform)) {
      if (deviceInfo.version === undefined) {
        console.log("no version on device?");
        return "";
      }
      const version = deviceInfo.version.split(".");
      const major = parseInt(version[0], 10);
      const majorMinor = version[1] === undefined ? 0 : parseInt(version[1], 10);
      if (major === 11 && majorMinor > 0 && majorMinor < 3) {
        return "_notch11";
      } else if (major >= 11) {
        return "_notch11_3";
      }
    }
  }

  // fallback to user agent. Maybe the code above is irrelevant?
  const iPhoneVersion = getIphoneVersionFromUserAgent();

  if (iPhoneVersion !== null) {
    if (iPhoneVersion.major === 11 && iPhoneVersion.minor > 0 && iPhoneVersion.minor < 3) {
      return "_notch11";
    }

    if (iPhoneVersion.major >= 11) {
      return "_notch11_3";
    }
  }

  return "";
}

export function getNotchCss(cssTag: string, def?: string): string {
  const notch = getCSSIOSSuffix();
  switch (notch) {
    case "_notch11":
    case "_notch11_3":
      return `${cssTag}${notch}`;
    case "":
    default:
      return def === undefined ? cssTag : def;
  }
}

export function getDeviceData() {
  let deviceData;
  const device = (window as any).device;

  if (device) {
    deviceData =
      device.platform + " " + device.version + ", " + device.manufacturer + " " + device.model;
  } else {
    deviceData = "NO DEVICE";
  }

  return deviceData;
}

export function getMedFlytLog(
  caregiver: AppCaregiver,
  agencies: Iterable<Agency>,
  epilogue?: string
) {
  const { agenciesString, summary } = parseAgencyAndStatusToEmailCode(agencies);

  const phoneNumbers = caregiver.phoneNumbers
    .map((p) => formatPhone(p.phoneNumber) + ` (${p.type})`)
    .join(", ");

  return `
MedFlyt App Version: ${AppVersion} (${getPlatform()})
Name: ${caregiver.firstName} ${caregiver.lastName}
Version: ${AppVersion}
Phone Numbers: ${phoneNumbers}
Device: ${getDeviceData()}
Agencies: ${agenciesString}
${summary}

${epilogue}
    `;
}

export function getDeviceUUID() {
  const device = (window as any).device;

  if (device) {
    return device.uuid;
  }

  return null;
}

export function getDeviceType(): "ios-safari" | "ios-mobile" | "android" | "other" {
  if (!("navigator" in window)) {
    return "other";
  }

  const userAgent = navigator.userAgent;

  if (userAgent.includes("iPhone") && userAgent.includes("Safari")) {
    return "ios-safari";
  }

  if (userAgent.includes("iPhone") || userAgent.includes("iPad")) {
    return "ios-mobile";
  }

  if (userAgent.includes("Android")) {
    return "android";
  }

  return "other";
}
